import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import { Toaster as Sonner } from 'sonner'

type ToasterProps = React.ComponentProps<typeof Sonner>

/**
 * Toasts on the app's own surface tokens.
 *
 * Sonner's `richColors` preset was dropped: it paints a solid red or green slab and follows
 * the OS colour scheme, so on a dark desktop an error toast turned into a maroon block over
 * a light page. The theme is pinned to light because the app is — when a dark theme lands
 * it is one edit in tokens.css and this follows.
 *
 * Tone is carried by the icon and a coloured left edge, the same way status badges do it.
 */
const Toaster = (props: ToasterProps) => (
  <Sonner
    theme="light"
    position="bottom-right"
    offset={16}
    gap={10}
    duration={5000}
    closeButton
    icons={{
      success: <CheckCircle2 className="size-4" />,
      error: <XCircle className="size-4" />,
      warning: <AlertTriangle className="size-4" />,
      info: <Info className="size-4" />,
    }}
    toastOptions={{
      unstyled: true,
      classNames: {
        toast:
          'group toast flex w-full items-start gap-3 rounded-md border border-border border-l-4 bg-surface-raised py-3 pr-3 pl-4 text-foreground shadow-e4',
        default: 'border-l-brand [&_[data-icon]]:text-brand',
        success: 'border-l-success [&_[data-icon]]:text-success',
        error: 'border-l-danger [&_[data-icon]]:text-danger',
        warning: 'border-l-warning [&_[data-icon]]:text-warning',
        info: 'border-l-info [&_[data-icon]]:text-info',
        icon: 'mt-0.5 flex shrink-0 items-center [&>svg]:size-4',
        content: 'flex min-w-0 flex-1 flex-col gap-0.5',
        title: 'text-sm leading-snug font-medium',
        description: 'text-xs leading-snug text-text-secondary',
        // Sonner pins its close button to the top-left corner; pull it into the row instead.
        // The flex centering matters: without it the X glyph sits off-centre in the hit area.
        closeButton:
          '!static !order-last !ml-auto !flex !size-6 !translate-x-0 !translate-y-0 !items-center !justify-center !rounded-md !border-0 !bg-transparent !text-text-muted hover:!bg-muted hover:!text-foreground',
        actionButton:
          'rounded-md bg-brand px-2.5 py-1 text-xs font-medium text-text-inverse hover:bg-brand/90',
        cancelButton:
          'rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-text-secondary hover:text-foreground',
      },
    }}
    {...props}
  />
)

export { Toaster }
